// 통계 탭의 "내 기록" 섹션 — 본인 PIN 로그인 후 본인에게만 보이는 영역.
//
// 비동기인 이유: 최근 N개 일정의 detail을 추가로 fetch해서 "최근 10경기"
// 카드까지 그려준다. 그 외 totals/월별/파트너 분석은 myData(서버 사전 집계)와
// details(상위에서 이미 fetch한 최근 5개)로 동기 계산.
//
// "강했던/약했던 상대"는 무승부만 본 상대를 잘못 잡지 않게 decided>0인
// 상대만 분류 대상.
//
// 유틸 헬퍼(StatCard, buildMonthlySparkline, buildTypeBar, buildPlayerList,
// aggregateSurfaceStats)는 ./index에서, 라벨 상수는 ui/format에서 가져온다.

import { Fragment } from "preact";
import { api } from "../api";
import { matchPlayerName } from "../matches";
import { navigate } from "../routes";
import { Icon } from "../ui/components/Icon";
import { StatCard } from "../ui/components/StatCard";
import { BACKHAND_STYLE_LABELS, DOMINANT_HAND_LABELS, SURFACE_LABELS } from "../ui/format";
import { DomNode, mountPreact } from "../ui/preact-mount";
import {
  aggregateSurfaceStats,
  buildMonthlySparkline,
  buildPlayerList,
  buildTypeBar,
} from "./index";

interface SummaryLike {
  event_id?: string;
  event_date?: string | null;
  event_title?: string | null;
  win_count?: number | null;
  loss_count?: number | null;
  draw_count?: number | null;
  completed_matches?: number | null;
  completed_mixed_doubles_count?: number | null;
  completed_men_doubles_count?: number | null;
  completed_women_doubles_count?: number | null;
  completed_exceptional_mix_count?: number | null;
}

interface MyData {
  event_summaries?: SummaryLike[];
}

interface MemberLike {
  member_id?: string;
  dominant_hand?: string | null;
  backhand_style?: string | null;
}

interface PlayerRecord {
  name: string;
  member_id?: string;
  wins: number;
  losses: number;
  draws: number;
}

interface RecentMatch {
  outcome: "win" | "loss" | "draw";
  eventDate: string;
  eventTitle: string;
  evIdx: number;
  matchIdx: number;
  partner: string | null;
  opponents: string | null;
  courtDisplay: string | null;
  score: string | null;
}

interface MyStatsViewModel {
  totals: {
    events: number;
    wins: number;
    losses: number;
    draws: number;
    matches: number;
    types: { mixed: number; men: number; women: number; exceptional: number };
  };
  winRate: number | null;
  attendEntries: [string, { wins: number; losses: number }][];
  monthEntries: [string, { wins: number; losses: number }][];
  topPartners: any[];
  strongOpp: any[];
  weakOpp: any[];
  surfaceTotals: ReturnType<typeof aggregateSurfaceStats>;
  recentSummaries: SummaryLike[];
  recentMatches: RecentMatch[];
}

function MyStatsEmpty() {
  return (
    <div class="section section--panel">
      <div class="section__title">
        <h3>내 기록</h3>
        <span class="badge badge--private">나만 보여요</span>
      </div>
      <p class="muted" style="text-align:center; padding:24px 0">아직 기록이 없어요</p>
    </div>
  );
}

function MyStatsView({ vm }: { vm: MyStatsViewModel }) {
  const { totals, winRate, attendEntries, monthEntries, topPartners, strongOpp, weakOpp,
    surfaceTotals, recentSummaries, recentMatches } = vm;
  const typeTotal = totals.types.mixed + totals.types.men + totals.types.women + totals.types.exceptional;
  const recordValue = totals.draws > 0
    ? `${totals.wins}-${totals.draws}-${totals.losses}`
    : `${totals.wins}-${totals.losses}`;
  const recordLabel = totals.draws > 0 ? "승-무-패" : "승-패";

  return (
    <div class="section section--panel">
      <div class="section__title">
        <h3>내 기록</h3>
        <span class="badge badge--private">나만 보여요</span>
      </div>
      <div class="stat-grid">
        <StatCard label="참여 일정" value={totals.events} />
        <StatCard label="경기" value={totals.matches} />
        <StatCard label={recordLabel} value={recordValue} />
        <StatCard label="승률" value={winRate == null ? "-" : `${winRate}%`} />
      </div>

      {attendEntries.length >= 2 && (
        <Fragment>
          <p class="stat-block__label">월별 참석율</p>
          <DomNode node={buildMonthlySparkline(attendEntries)} />
        </Fragment>
      )}

      {monthEntries.length >= 2 && (
        <Fragment>
          <p class="stat-block__label">월별 승률</p>
          <DomNode node={buildMonthlySparkline(monthEntries)} />
        </Fragment>
      )}

      {typeTotal > 0 && (
        <Fragment>
          <p class="stat-block__label">매치 타입 분포</p>
          <DomNode node={buildTypeBar(totals.types, typeTotal)} />
        </Fragment>
      )}

      {topPartners.length > 0 && (
        <Fragment>
          <p class="stat-block__label">자주 뛴 파트너 TOP</p>
          <DomNode node={buildPlayerList(topPartners)} />
        </Fragment>
      )}

      {(strongOpp.length > 0 || weakOpp.length > 0) ? (
        <Fragment>
          <p class="stat-block__label">강한 · 약한 상대 (최근 5개 일정)</p>
          <div class="stat-split">
            <div class="stat-split__col">
              <p class="stat-split__title">강했던 상대</p>
              {strongOpp.length > 0
                ? <DomNode node={buildPlayerList(strongOpp)} />
                : <p class="muted small">데이터 부족</p>}
            </div>
            <div class="stat-split__col">
              <p class="stat-split__title">약했던 상대</p>
              {weakOpp.length > 0
                ? <DomNode node={buildPlayerList(weakOpp)} />
                : <p class="muted small">데이터 부족</p>}
            </div>
          </div>
        </Fragment>
      ) : (
        <p class="muted small">파트너·상대 분석은 최근 완료된 일정 5개 기준으로 2경기 이상 함께 한 경우에만 표시돼요.</p>
      )}

      {surfaceTotals.length > 0 && (
        <Fragment>
          <p class="stat-block__label" style="margin-top:16px">코트 유형별 기록</p>
          <div class="surface-stats">
            {surfaceTotals.map((row) => {
              const pct = row.decided > 0 ? Math.round((row.wins / row.decided) * 100) : null;
              const meta = [
                `${row.wins}승 ${row.losses}패`,
                row.draws > 0 ? ` · 무 ${row.draws}` : "",
                pct != null ? ` · 승률 ${pct}%` : "",
              ].filter(Boolean).join("");
              return (
                <div key={row.surface} class="surface-stats__row">
                  <div class="surface-stats__head">
                    <span class={`surface-stats__badge surface-stats__badge--${row.surface}`}>{row.label}</span>
                    <span class="surface-stats__count">{row.matches}경기</span>
                  </div>
                  <div class="surface-stats__meta">{meta}</div>
                </div>
              );
            })}
          </div>
        </Fragment>
      )}

      {recentSummaries.length > 0 && (
        <Fragment>
          <p class="stat-block__label" style="margin-top:16px">
            일정별 기록 (최근 {recentSummaries.length}개)
          </p>
          <div class="list list--compact">
            {recentSummaries.map((s) => {
              const sDecided = Number(s.win_count || 0) + Number(s.loss_count || 0);
              const sRate = sDecided > 0 ? Math.round((Number(s.win_count || 0) / sDecided) * 100) : null;
              const meta = [
                s.event_date || "",
                s.completed_matches ? ` · ${s.completed_matches}경기` : "",
                s.win_count != null ? ` · ${s.win_count}승 ${s.loss_count || 0}패` : "",
                sRate != null ? ` · ${sRate}%` : "",
              ].filter(Boolean).join("");
              return (
                <button
                  key={s.event_id}
                  type="button"
                  class="list__item"
                  onClick={() => navigate({ name: "event", id: s.event_id })}
                >
                  <div class="list__main">
                    <p class="list__title">{s.event_title || "일정"}</p>
                    <p class="list__meta">{meta}</p>
                  </div>
                  <div class="list__trail"><Icon name="chevron" size={16} /></div>
                </button>
              );
            })}
          </div>
        </Fragment>
      )}

      {recentMatches.length > 0 && (
        <Fragment>
          <p class="stat-block__label" style="margin-top:16px">최근 {recentMatches.length}경기</p>
          <div class="list list--compact">
            {[...recentMatches].reverse().map((mm, i) => {
              const cls = mm.outcome === "win" ? "badge badge--win"
                : mm.outcome === "loss" ? "badge badge--loss"
                : "badge badge--draw";
              const label = mm.outcome === "win" ? "승" : mm.outcome === "loss" ? "패" : "무";
              const detailParts: string[] = [];
              if (mm.partner) detailParts.push(`파트너: ${mm.partner}`);
              if (mm.opponents) detailParts.push(`상대: ${mm.opponents}`);
              const metaParts: string[] = [];
              if (mm.score) metaParts.push(mm.score);
              if (mm.courtDisplay) metaParts.push(mm.courtDisplay);
              return (
                <div key={i} class="schedule-row">
                  <span class={cls} style="flex-shrink:0">{label}</span>
                  <div class="schedule-row__info">
                    <span class="schedule-row__type">{mm.eventTitle || "일정"}</span>
                    {detailParts.length > 0 && (
                      <span class="schedule-row__detail">{detailParts.join(" · ")}</span>
                    )}
                    <span class="schedule-row__court">
                      {mm.eventDate}{metaParts.length > 0 ? ` · ${metaParts.join(" · ")}` : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Fragment>
      )}
    </div>
  );
}

export async function buildMyStatsSection(
  myData: MyData | null,
  details: any[],
  memberId: string,
  members: MemberLike[] | null,
  allCompletedEvents: any[] | null,
): Promise<HTMLElement> {
  const summaries = (myData?.event_summaries || []).slice();
  if (summaries.length === 0) {
    return mountPreact(<MyStatsEmpty />);
  }
  summaries.sort((a, b) => String(a.event_date || "").localeCompare(String(b.event_date || "")));

  const totals = summaries.reduce((acc, s) => {
    acc.events += 1;
    acc.wins += Number(s.win_count || 0);
    acc.losses += Number(s.loss_count || 0);
    acc.draws += Number(s.draw_count || 0);
    acc.matches += Number(s.completed_matches || 0);
    acc.types.mixed += Number(s.completed_mixed_doubles_count || 0);
    acc.types.men += Number(s.completed_men_doubles_count || 0);
    acc.types.women += Number(s.completed_women_doubles_count || 0);
    acc.types.exceptional += Number(s.completed_exceptional_mix_count || 0);
    return acc;
  }, { events: 0, wins: 0, losses: 0, draws: 0, matches: 0, types: { mixed: 0, men: 0, women: 0, exceptional: 0 } });
  const decided = totals.wins + totals.losses;
  const winRate = decided > 0 ? Math.round((totals.wins / decided) * 100) : null;

  const byMonth = new Map<string, { wins: number; losses: number }>();
  for (const s of summaries) {
    const ym = (s.event_date || "").slice(0, 7);
    if (!ym) continue;
    if (!byMonth.has(ym)) byMonth.set(ym, { wins: 0, losses: 0 });
    const cell = byMonth.get(ym)!;
    cell.wins += Number(s.win_count || 0);
    cell.losses += Number(s.loss_count || 0);
  }
  const monthEntries = [...byMonth.entries()].sort((a, b) => a[0].localeCompare(b[0])).slice(-6);

  const partners = new Map<string, PlayerRecord>();
  const opponents = new Map<string, PlayerRecord>();
  for (const d of details) {
    const myParticipant = (d.participants || []).find((p: any) => p.member_id === memberId);
    if (!myParticipant) continue;
    const myPid = myParticipant.event_participant_id;
    for (const m of (d.matches || [])) {
      if (!m.winner_side) continue;
      const mySide = (m.sides || []).find((s: any) => (s.player_ids || []).includes(myPid));
      if (!mySide) continue;
      const oppSide = (m.sides || []).find((s: any) => s !== mySide);
      const iWon = (mySide.side_number === 1 && m.winner_side === "side_a")
        || (mySide.side_number === 2 && m.winner_side === "side_b");
      const draw = m.winner_side === "draw";
      const bump = (map: Map<string, PlayerRecord>, p: any, win: boolean, isDraw: boolean) => {
        if (!p || !p.event_participant_id || p.event_participant_id === myPid) return;
        const inner = p.participant || p;
        const key = inner.member_id || p.event_participant_id;
        if (!map.has(key)) map.set(key, { name: matchPlayerName(p), member_id: inner.member_id, wins: 0, losses: 0, draws: 0 });
        const rec = map.get(key)!;
        if (isDraw) rec.draws++; else if (win) rec.wins++; else rec.losses++;
      };
      for (const p of (mySide.players || [])) bump(partners, p, iWon, draw);
      for (const p of ((oppSide && oppSide.players) || [])) bump(opponents, p, iWon, draw);
    }
  }
  const memberById = new Map<string, MemberLike>();
  if (members) for (const m of members) if (m.member_id) memberById.set(m.member_id, m);
  const handTag = (mid: string | undefined): string | null => {
    if (!mid) return null;
    const m = memberById.get(mid);
    if (!m) return null;
    const parts = [
      m.dominant_hand ? DOMINANT_HAND_LABELS[m.dominant_hand] : null,
      m.backhand_style ? BACKHAND_STYLE_LABELS[m.backhand_style] : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : null;
  };
  const rankPlayers = (map: Map<string, PlayerRecord>, withTag: boolean) => [...map.values()]
    .map((r) => ({
      ...r,
      total: r.wins + r.losses + r.draws,
      rate: (r.wins + r.losses) > 0 ? r.wins / (r.wins + r.losses) : 0,
      tag: withTag ? handTag(r.member_id) : null,
    }))
    .filter((r) => r.total >= 2);
  const topPartners = rankPlayers(partners, false).sort((a, b) => b.total - a.total || b.rate - a.rate).slice(0, 3);
  const allOpps = rankPlayers(opponents, true);
  const decidedOpps = allOpps.filter((r) => (r.wins + r.losses) > 0);
  const strongOpp = decidedOpps.filter((r) => r.rate > 0.5).sort((a, b) => b.rate - a.rate || b.total - a.total).slice(0, 3);
  const weakOpp = decidedOpps.filter((r) => r.rate < 0.5).sort((a, b) => a.rate - b.rate || b.total - a.total).slice(0, 3);

  // 월별 참석율
  let attendEntries: [string, { wins: number; losses: number }][] = [];
  if (allCompletedEvents && allCompletedEvents.length > 0) {
    const clubByMonth = new Map<string, number>();
    for (const ev of allCompletedEvents) {
      const ym = (ev.event_date || "").slice(0, 7);
      if (ym) clubByMonth.set(ym, (clubByMonth.get(ym) || 0) + 1);
    }
    const myByMonth = new Map<string, number>();
    for (const s of summaries) {
      const ym = (s.event_date || "").slice(0, 7);
      if (ym) myByMonth.set(ym, (myByMonth.get(ym) || 0) + 1);
    }
    attendEntries = [...clubByMonth.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([ym, total]) => [ym, { wins: myByMonth.get(ym) || 0, losses: total - (myByMonth.get(ym) || 0) }]);
  }

  const surfaceTotals = aggregateSurfaceStats(summaries);

  const sortedSummaries = summaries.slice().sort(
    (a, b) => String(b.event_date || "").localeCompare(String(a.event_date || "")),
  );
  const recentSummaries = sortedSummaries.slice(0, 5);

  // 최근 10경기 — 최근 일정의 detail을 fetch해서 매치 단위로 펼침.
  const recentMatchTarget = 10;
  const eventsToFetch: SummaryLike[] = [];
  let accMatches = 0;
  for (const s of sortedSummaries) {
    if (accMatches >= recentMatchTarget) break;
    eventsToFetch.push(s);
    accMatches += Number(s.win_count || 0) + Number(s.loss_count || 0) + Number(s.draw_count || 0);
  }
  const detailCache = new Map<string, any>();
  for (const d of details) {
    const id = d?.event?.event_id;
    if (id) detailCache.set(id, d);
  }
  const detailsRaw = await Promise.all(
    eventsToFetch.map((s) => {
      const cached = detailCache.get(s.event_id);
      if (cached) return cached;
      return api(`/api/v1/events/${s.event_id}`).catch(() => null);
    }),
  );
  const myMatches: RecentMatch[] = [];
  eventsToFetch.forEach((s, evIdx) => {
    const detail = detailsRaw[evIdx];
    if (!detail) return;
    const myParticipant = (detail.participants || []).find((p: any) => p.member_id === memberId);
    if (!myParticipant) return;
    const myPid = myParticipant.event_participant_id;
    const detailCourts = detail.courts || [];
    const surfaceByCourtId = new Map<string, string>(
      detailCourts.filter((c: any) => c.surface_type).map((c: any) => [c.event_court_id, c.surface_type]),
    );
    (detail.matches || []).forEach((m: any, matchIdx: number) => {
      try {
        if (!m.winner_side) return;
        const mySide = (m.sides || []).find((sd: any) => (sd.player_ids || []).includes(myPid));
        if (!mySide) return;
        const outcome: RecentMatch["outcome"] = m.winner_side === "draw" ? "draw"
          : ((mySide.side_number === 1 && m.winner_side === "side_a")
            || (mySide.side_number === 2 && m.winner_side === "side_b")) ? "win" : "loss";
        const partner = (mySide.players || [])
          .filter((pl: any) => (pl.event_participant_id || pl.participant?.event_participant_id) !== myPid)
          .map((pl: any) => matchPlayerName(pl))
          .join(", ");
        const oppNames = (m.sides || [])
          .filter((sd: any) => sd !== mySide)
          .flatMap((sd: any) => (sd.players || []).map((pl: any) => matchPlayerName(pl)))
          .join(", ");
        const sf = m.event_court_id ? surfaceByCourtId.get(m.event_court_id) : null;
        let courtSurface: string | null = sf ? (SURFACE_LABELS[sf] || sf) : null;
        if (!courtSurface && m.court_label) {
          for (const label of Object.values(SURFACE_LABELS)) {
            if (m.court_label.includes(label)) { courtSurface = label; break; }
          }
        }
        const courtDisplay = courtSurface || (m.court_label ? `코트 ${m.court_label}` : null);
        const myScore = mySide.side_number === 1 ? m.side_a_score : m.side_b_score;
        const oppScore = mySide.side_number === 1 ? m.side_b_score : m.side_a_score;
        const score = myScore != null && oppScore != null ? `${myScore}:${oppScore}` : null;
        myMatches.push({
          outcome,
          eventDate: s.event_date || "",
          eventTitle: s.event_title || "",
          evIdx,
          matchIdx,
          partner: partner || null,
          opponents: oppNames || null,
          courtDisplay,
          score,
        });
      } catch (e) {
        console.error("recentMatch parse error", e, m);
      }
    });
  });
  myMatches.sort((a, b) => {
    if (a.evIdx !== b.evIdx) return a.evIdx - b.evIdx;
    return b.matchIdx - a.matchIdx;
  });
  const recentMatches = myMatches.slice(0, recentMatchTarget).reverse();

  const vm: MyStatsViewModel = {
    totals,
    winRate,
    attendEntries,
    monthEntries,
    topPartners,
    strongOpp,
    weakOpp,
    surfaceTotals,
    recentSummaries,
    recentMatches,
  };

  return mountPreact(<MyStatsView vm={vm} />);
}
